import type {
  BlindComparisonResponse,
  ConversationEval,
  RunConversationEvalResponse,
} from 'services/types/conversationEvals';

import baseApi from './baseApi';

const conversationEvalsApi = baseApi.injectEndpoints({
  endpoints: (build) => ({
    getAllConversationEvals: build.query({
      query: (params?: { businessUnitIds?: number[] }) => ({
        url: '/admin/conversationEvals/all',
        method: 'GET',
        params,
      }),
      transformResponse: (response: ConversationEval[]): ConversationEval[] => response,
    }),
    getOneConversationEval: build.query({
      query: (conversationEvalId: number) => ({
        url: `/admin/conversationEvals/${conversationEvalId}`,
        method: 'GET',
      }),
      transformResponse: (response: ConversationEval): ConversationEval => response,
    }),
    createConversationEval: build.mutation({
      query: (conversationEval: Omit<ConversationEval, 'id' | 'createdAt' | 'updatedAt'>) => ({
        url: '/admin/conversationEvals',
        method: 'POST',
        body: conversationEval,
      }),
      transformResponse: (response: ConversationEval): ConversationEval => response,
    }),
    createBulkConversationEvals: build.mutation({
      query: (conversationEvals: Omit<ConversationEval, 'id' | 'createdAt' | 'updatedAt'>[]) => ({
        url: '/admin/conversationEvals/bulk',
        method: 'POST',
        body: conversationEvals,
      }),
    }),
    updateConversationEval: build.mutation({
      query: ({ conversationEvalId, ...data }: Partial<ConversationEval> & { conversationEvalId: number }) => ({
        url: `/admin/conversationEvals/${conversationEvalId}`,
        method: 'PATCH',
        body: data,
      }),
      transformResponse: (response: ConversationEval): ConversationEval => response,
    }),
    deleteConversationEval: build.mutation({
      query: (conversationEvalId: number) => ({
        url: `/admin/conversationEvals/${conversationEvalId}`,
        method: 'DELETE',
      }),
    }),
    getEvalFromMessage: build.mutation({
      query: (props: { messageId: number }) => ({
        url: `/admin/conversationEvals/getEvalFromMessage/${props.messageId}`,
        method: 'GET',
      }),
      transformResponse: (response: ConversationEval) => response,
    }),
    runConversationEval: build.mutation({
      query: (params: {
        conversationEvalIds?: number[];
        businessUnitIds?: number[];
        completionAIModel?: string;
        evaluationAIModel?: string;
        threshold?: number;
        /** Prompt template to grade against instead of the business unit's stored one. */
        agentPrompt?: string;
      }) => ({
        url: `/admin/agentEvals/conversations`,
        method: 'POST',
        body: params,
      }),
      transformResponse: (response: RunConversationEvalResponse) => response,
    }),

    runBlindComparisonEval: build.mutation({
      query: (params: {
        conversationEvalId: number;
        models: string[];
        evaluationModel?: string;
        threshold?: number;
        businessUnitIds?: number[];
        customPrompt?: string;
      }) => ({
        url: `/admin/agentEvals/blindComparisons/${params.conversationEvalId}`,
        method: 'POST',
        body: {
          models: params.models,
          evaluationModel: params.evaluationModel,
          threshold: params.threshold,
          businessUnitIds: params.businessUnitIds,
          customPrompt: params.customPrompt,
        },
      }),
      transformResponse: (response: BlindComparisonResponse) => response,
    }),

    runSingleModelEval: build.mutation({
      query: (params: {
        conversationEvalId: number;
        targetModel: string;
        evaluationModel?: string;
        threshold?: number;
        businessUnitIds?: number[];
        customPrompt?: string;
      }) => ({
        url: `/admin/agentEvals/singleModel/${params.conversationEvalId}`,
        method: 'POST',
        body: {
          targetModel: params.targetModel,
          evaluationModel: params.evaluationModel,
          threshold: params.threshold,
          businessUnitIds: params.businessUnitIds,
          customPrompt: params.customPrompt,
        },
      }),
      transformResponse: (response: unknown) => response,
    }),

    uploadFile: build.mutation({
      query: (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return {
          url: '/admin/conversationEvals/upload-file',
          method: 'POST',
          body: formData,
        };
      },
      transformResponse: (response: {
        fileUrl: string;
        fileName: string;
        mediaId: string;
        originalName: string;
        size: number;
        mimeType: string;
      }) => response,
    }),
  }),
  overrideExisting: false,
});

export const {
  useGetAllConversationEvalsQuery,
  useGetOneConversationEvalQuery,
  useCreateConversationEvalMutation,
  useCreateBulkConversationEvalsMutation,
  useUpdateConversationEvalMutation,
  useDeleteConversationEvalMutation,
  useGetEvalFromMessageMutation,
  useRunConversationEvalMutation,
  useRunBlindComparisonEvalMutation,
  useRunSingleModelEvalMutation,
  useUploadFileMutation,
} = conversationEvalsApi;
